import React from 'react';
import { Alert, Card, Col, List, Row, Space, Statistic, Tag, Typography } from 'antd';
import type { ProjectOperatingInsights } from '../../services/statisticsService';

const { Text, Title } = Typography;

interface OperatingInsightsPanelProps {
  insights: ProjectOperatingInsights | null;
  projectId: string;
  isMobile?: boolean;
}

const OperatingInsightsPanel: React.FC<OperatingInsightsPanelProps> = ({
  insights,
  projectId,
  isMobile = false,
}) => {
  if (!insights) return null;

  const { dashboard, businessChain, workItemRanking, riskAlerts } = insights;
  const budgetAlertType = dashboard.budgetStatus.level === 'healthy'
    ? 'success'
    : dashboard.budgetStatus.level === 'warning'
      ? 'warning'
      : 'error';
  const openWorkItem = (itemNo?: string) => {
    const params = new URLSearchParams({ projectId });
    if (itemNo) params.set('itemNo', itemNo);
    window.location.href = `/progress/work-items?${params.toString()}`;
  };

  return (
    <div
      style={{
        marginBottom: isMobile ? 16 : 24,
        padding: isMobile ? 14 : 18,
        borderRadius: 20,
        background: 'rgba(210, 218, 224, 0.5)',
        border: '1px solid rgba(16, 27, 35, 0.08)'
      }}
    >
      <Title level={5} style={{ marginBottom: 16, fontSize: isMobile ? 14 : 16, color: '#10171d' }}>
        经营分析与预警
      </Title>

      <Row gutter={[isMobile ? 10 : 16, isMobile ? 10 : 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 16, background: 'rgba(84, 117, 144, 0.08)', border: '1px solid rgba(16, 27, 35, 0.06)' }}>
            <Statistic title="预计利润" value={dashboard.operatingSummary.estimatedProfit} precision={2} prefix="¥" valueStyle={{ color: dashboard.operatingSummary.estimatedProfit >= 0 ? '#547590' : '#cf1322', fontSize: isMobile ? 18 : 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 16, background: 'rgba(90, 209, 178, 0.08)', border: '1px solid rgba(16, 27, 35, 0.06)' }}>
            <Statistic title="预算剩余" value={dashboard.operatingSummary.budgetRemaining} precision={2} prefix="¥" valueStyle={{ color: '#5ad1b2', fontSize: isMobile ? 18 : 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 16, background: 'rgba(185, 146, 88, 0.08)', border: '1px solid rgba(16, 27, 35, 0.06)' }}>
            <Statistic title="高风险项" value={businessChain.highRiskCount} suffix="项" valueStyle={{ color: '#b99258', fontSize: isMobile ? 18 : 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 16, background: 'rgba(147, 132, 255, 0.08)', border: '1px solid rgba(16, 27, 35, 0.06)' }}>
            <Statistic title="已结算整改" value={businessChain.settledRectificationCount} suffix="项" valueStyle={{ color: '#9384ff', fontSize: isMobile ? 18 : 22 }} />
          </Card>
        </Col>
      </Row>

      <Alert
        type={budgetAlertType}
        showIcon
        message={dashboard.budgetStatus.message}
        style={{ marginBottom: 16, borderRadius: 14 }}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card size="small" title="风险工程项" style={{ borderRadius: 16 }}>
            <List
              locale={{ emptyText: '暂无风险工程项' }}
              dataSource={workItemRanking.slice(0, 3)}
              renderItem={(item) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <Space size={8} wrap style={{ marginBottom: 4 }}>
                      <Text strong style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => openWorkItem(item.itemNo)}>{item.itemNo || '未编号'}</Text>
                      <Text style={{ cursor: 'pointer' }} onClick={() => openWorkItem(item.itemNo)}>{item.name}</Text>
                      <Tag color={item.warningLevel === 'high' ? 'red' : item.warningLevel === 'medium' ? 'orange' : 'blue'}>
                        {item.warningLevel === 'high' ? '高风险' : item.warningLevel === 'medium' ? '中风险' : '低风险'}
                      </Tag>
                    </Space>
                    <div style={{ color: '#667986', fontSize: 12 }}>
                      风险分 {item.riskScore} · 毛利差 ¥{item.grossMarginGap.toLocaleString()}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card size="small" title="预警摘要" style={{ borderRadius: 16 }}>
            <List
              locale={{ emptyText: '暂无预警信息' }}
              dataSource={riskAlerts.slice(0, 3)}
              renderItem={(item) => (
                <List.Item>
                  <div>
                    <Space size={8} wrap>
                      <Text strong style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => openWorkItem(item.itemNo)}>{item.itemNo || '未编号'}</Text>
                      <Text style={{ cursor: 'pointer' }} onClick={() => openWorkItem(item.itemNo)}>{item.name}</Text>
                      <Tag color={item.warningLevel === 'high' ? 'red' : 'orange'}>
                        {item.warningLevel === 'high' ? '高风险' : '中风险'}
                      </Tag>
                    </Space>
                    <div style={{ color: '#667986', fontSize: 12, marginTop: 4 }}>
                      {item.reasons.join('；') || '请关注该工程项经营状态'}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OperatingInsightsPanel;
